import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, ListTree } from "lucide-react";

const TYPES = [["ASSET", "Aset"], ["LIABILITY", "Kewajiban"], ["EQUITY", "Ekuitas"], ["REVENUE", "Pendapatan"], ["COGS", "HPP"], ["EXPENSE", "Beban"], ["OTHER", "Lain-lain"]];

export default function ChartOfAccounts() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const canManage = can("accounting.coa.manage");
  const load = () => { setRows(null); api.get("/accounting/chart-of-accounts").then((r) => setRows(r.data)); };
  useEffect(load, []);

  return (
    <div>
      <PageHeader title="Chart of Accounts" subtitle="Daftar akun (double-entry). Akun header tidak bisa diposting." testId="coa-header">
        {canManage && <Button onClick={() => setOpen(true)} data-testid="add-coa-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1.5 h-4 w-4" /> Akun Baru</Button>}
      </PageHeader>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="Belum ada akun" icon={ListTree} /> : (
          <Table>
            <TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Nama Akun</TableHead><TableHead>Tipe</TableHead><TableHead>Saldo Normal</TableHead><TableHead>Posting</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.id} data-testid={`coa-row-${a.code}`} className={a.allow_posting ? "" : "bg-slate-50/60"}>
                  <TableCell className="font-mono text-xs font-semibold">{a.code}</TableCell>
                  <TableCell style={{ paddingLeft: `${(a.code.length - 4) * 12 + 12}px` }} className={a.allow_posting ? "" : "font-semibold text-slate-700"}>{a.name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{(TYPES.find((t) => t[0] === a.type) || [])[1] || a.type}</TableCell>
                  <TableCell className="text-xs">{a.normal_balance === "DEBIT" ? "Debit" : "Kredit"}</TableCell>
                  <TableCell>{a.allow_posting ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600">Bisa Posting</span> : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">Header</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {open && <COADialog accounts={rows || []} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function COADialog({ accounts, onClose, onSaved }) {
  const [f, setF] = useState({ code: "", name: "", type: "EXPENSE", parent_code: "", allow_posting: true, description: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.code || !f.name) return toast.error("Kode & nama wajib diisi");
    setSaving(true);
    try { await api.post("/accounting/chart-of-accounts", { ...f, parent_code: f.parent_code || null }); toast.success("Akun dibuat"); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">Akun Baru</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs text-slate-500">Kode</Label><Input className="mt-1" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="coa-code" /></div>
          <div><Label className="text-xs text-slate-500">Tipe</Label><Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger className="mt-1" data-testid="coa-type"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t[0]} value={t[0]}>{t[1]}</SelectItem>)}</SelectContent></Select></div>
          <div className="col-span-2"><Label className="text-xs text-slate-500">Nama Akun</Label><Input className="mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="coa-name" /></div>
          <div className="col-span-2"><Label className="text-xs text-slate-500">Induk (opsional)</Label><Select value={f.parent_code || "__none"} onValueChange={(v) => setF({ ...f, parent_code: v === "__none" ? "" : v })}><SelectTrigger className="mt-1" data-testid="coa-parent"><SelectValue placeholder="Tanpa induk" /></SelectTrigger><SelectContent><SelectItem value="__none">Tanpa induk</SelectItem>{accounts.filter((a) => !a.allow_posting).map((a) => <SelectItem key={a.code} value={a.code}>{a.code} - {a.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="col-span-2"><Label className="text-xs text-slate-500">Bisa Posting?</Label><Select value={f.allow_posting ? "1" : "0"} onValueChange={(v) => setF({ ...f, allow_posting: v === "1" })}><SelectTrigger className="mt-1" data-testid="coa-posting"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="1">Ya (akun transaksi)</SelectItem><SelectItem value="0">Tidak (akun header)</SelectItem></SelectContent></Select></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="save-coa-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
